/**
 * AUTH-002 §6.1 item 2 — activity is shared across tabs.
 *
 * Each tab records its last input time in localStorage; every tab reads the newest value
 * across all of them. Idle therefore means "no input in ANY tab", and a background tab
 * can no longer sign the user out while they work in another one. Reading storage on
 * every check needs no message passing; the `storage` event is not required.
 *
 * Pure: the store and the clock are injected so the unit tests can drive two "tabs"
 * against one shared store.
 */
import { ACTIVITY_WRITE_THROTTLE_MS, LAST_ACTIVITY_KEY } from './session-policy';

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ActivityTracker {
  /** Record input in this tab. Writes to the shared store at most once per throttle window unless forced. */
  markActivity(force?: boolean): void;
  /** Write this tab's newest activity if the throttle held it back (call on pagehide). */
  flush(): void;
  /** Newest input time across every tab, in ms since epoch; 0 when nothing was ever recorded. */
  lastActivityAt(): number;
  /** The shared value alone (what other tabs have written), 0 when absent. */
  sharedActivityAt(): number;
}

export function createActivityTracker(deps: {
  store: KeyValueStore | null;
  now: () => number;
  throttleMs?: number;
}): ActivityTracker {
  const throttleMs = deps.throttleMs ?? ACTIVITY_WRITE_THROTTLE_MS;
  let local = 0;
  let lastWriteAt = 0;
  let written = 0;

  const readShared = (): number => {
    try {
      const v = Number(deps.store?.getItem(LAST_ACTIVITY_KEY));
      return Number.isFinite(v) && v > 0 ? v : 0;
    } catch {
      return 0;
    }
  };

  const write = (at: number) => {
    try {
      // Never move the shared value backwards: another tab may have written a newer one.
      const value = Math.max(at, readShared());
      deps.store?.setItem(LAST_ACTIVITY_KEY, String(value));
      written = value;
    } catch {
      // storage unavailable: this tab still tracks its own activity in memory
    }
  };

  return {
    markActivity(force = false) {
      const t = deps.now();
      local = Math.max(local, t);
      if (force || t - lastWriteAt >= throttleMs) {
        lastWriteAt = t;
        write(local);
      }
    },
    flush() {
      if (local > written) write(local);
    },
    lastActivityAt() {
      return Math.max(local, readShared());
    },
    sharedActivityAt() {
      return readShared();
    },
  };
}
